import axios from 'axios';
import { SERVER_NAME } from '../config';

// Session timeout in milliseconds (15 minutes)
const SESSION_TIMEOUT = 15 * 60 * 1000;

/**
 * SessionManager handles user session tracking and token refresh
 * to prevent automatic logouts during user activity
 */
class SessionManager {
  constructor() {
    this.sessionTimer = null;
    this.lastActivity = Date.now();
    this.isActive = false;
    this.authCallback = null;
  }

  /**
   * Initialize the session manager
   * @param {Function} authCallback - Function to call when auth state changes (logout)
   */
  initialize(authCallback) {
    this.authCallback = authCallback;
    this.startActivityTracking();
    this.scheduleTokenRefresh();

    console.log('SessionManager initialized');
    return this;
  }

  /**
   * Record user activity to prevent session timeout
   */
  recordActivity() {
    this.lastActivity = Date.now();
    
    // If we're not already active, start refresh cycle
    if (!this.isActive) {
      this.isActive = true;
      this.scheduleTokenRefresh();
    }
  }

  /**
   * Schedule a token refresh at regular intervals while the user is active
   */
  scheduleTokenRefresh() {
    // Clear any existing timer
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    // Check if user is authenticated
    if (!localStorage.getItem('isAuthenticated')) {
      return;
    }

    // Schedule refresh for session tokens
    this.sessionTimer = setTimeout(async () => {
      // If user has been inactive for too long, don't refresh
      const inactiveTime = Date.now() - this.lastActivity;
      
      if (inactiveTime > SESSION_TIMEOUT) {
        console.log('User inactive, not refreshing session');
        this.isActive = false;
        return;
      }
      
      try {
        // Call server endpoint to refresh session
        const response = await axios.get(`${SERVER_NAME}/refresh-session`, { 
          withCredentials: true 
        });
        
        if (response.data.success) {
          console.log('Session refreshed successfully');
          // Schedule next refresh
          this.scheduleTokenRefresh();
        } else {
          console.log('Session refresh failed, logging out');
          this.handleLogout();
        }
      } catch (error) {
        const status = error && error.response ? error.response.status : null;
        if (status === 401 || status === 403) {
          console.log('Session refresh unauthorized, logging out');
          this.handleLogout();
          return;
        }
        console.error('Error refreshing session:', error);
        // Don't log out immediately on network error, try again later
        setTimeout(() => this.scheduleTokenRefresh(), 30000);
      }
    }, SESSION_TIMEOUT / 2); // Refresh halfway through timeout period
  }

  /**
   * Start tracking user activity across the application
   */
  startActivityTracking() {
    // Record activity on user interactions
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    
    const activityHandler = () => {
      this.recordActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, activityHandler, { passive: true });
    });

    // Initial activity record
    this.recordActivity();
  }

  /**
   * Handle logout when session expires
   */
  handleLogout() {
    if (this.authCallback) {
      this.authCallback();
    }
  }

  /**
   * Clean up resources when component unmounts
   */
  cleanup() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
